<template>
  <el-dialog
    v-model="visible"
    :title="$t('chat.settings.title')"
    width="420px"
    :append-to-body="true"
    :close-on-click-modal="false"
    destroy-on-close
    @open="onOpen"
  >
    <div class="space-y-4">
      <!-- Shareable chat id — used by friends to send requests without having
           to know your email or exact name. Generated lazily on first chat
           connect. Styled as a chip with a click-to-copy affordance. -->
      <div>
        <label class="block text-xs font-semibold text-text-muted mb-1.5">
          {{ $t('chat.settings.chat_id_label') }}
        </label>
        <div
          v-if="displayedChatId"
          class="flex items-center gap-2 px-3 py-2 rounded-md bg-black/30 border border-white/5"
        >
          <span class="flex-1 font-mono text-sm tracking-widest text-white select-all">
            {{ displayedChatIdFormatted }}
          </span>
          <button
            type="button"
            class="shrink-0 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded text-text-muted hover:text-white hover:bg-white/5 transition-colors"
            @click="copyChatId"
          >
            {{ copied ? $t('chat.settings.chat_id_copied') : $t('chat.settings.chat_id_copy') }}
          </button>
        </div>
        <p v-else class="text-[11px] text-text-dim">{{ $t('chat.settings.chat_id_pending') }}</p>
        <p class="text-[11px] text-text-dim mt-1">{{ $t('chat.settings.chat_id_hint') }}</p>
      </div>

      <div>
        <label class="block text-xs font-semibold text-text-muted mb-1.5">
          {{ $t('chat.settings.language_label') }}
        </label>
        <el-select
          v-model="selected"
          class="w-full"
          :placeholder="$t('chat.settings.language_off')"
          filterable
        >
          <el-option
            :label="$t('chat.settings.language_off')"
            :value="OFF_VALUE"
          />
          <el-option
            v-for="lang in supportedLanguages"
            :key="lang.code"
            :label="lang.label"
            :value="lang.code"
          />
        </el-select>
        <p class="text-[11px] text-text-dim mt-2">{{ $t('chat.settings.language_hint') }}</p>
      </div>

      <!-- ──────────── Profile ──────────── -->
      <div class="space-y-3">
        <label class="block text-xs font-semibold text-text-muted">{{ $t('chat.settings.profile_section') }}</label>
        <div>
          <label class="block text-[11px] text-text-dim mb-1">{{ $t('chat.settings.display_name') }}</label>
          <input
            v-model="displayName"
            type="text"
            maxlength="100"
            :placeholder="$t('chat.settings.display_name_ph')"
            class="w-full bg-black/30 border border-white/5 rounded-md px-3 py-2 text-sm text-white placeholder:text-text-dim focus:outline-none focus:border-primary/40"
          />
        </div>
        <div>
          <label class="block text-[11px] text-text-dim mb-1">{{ $t('chat.settings.custom_status') }}</label>
          <input
            v-model="customStatus"
            type="text"
            maxlength="100"
            :placeholder="$t('chat.settings.custom_status_ph')"
            class="w-full bg-black/30 border border-white/5 rounded-md px-3 py-2 text-sm text-white placeholder:text-text-dim focus:outline-none focus:border-primary/40"
          />
        </div>
        <div>
          <label class="block text-[11px] text-text-dim mb-1">{{ $t('chat.settings.bio') }}</label>
          <textarea
            v-model="bio"
            rows="3"
            maxlength="500"
            :placeholder="$t('chat.settings.bio_ph')"
            class="w-full bg-black/30 border border-white/5 rounded-md px-3 py-2 text-sm text-white placeholder:text-text-dim focus:outline-none focus:border-primary/40 resize-none"
          ></textarea>
          <p class="text-[10px] text-text-dim mt-1 text-right tabular-nums">{{ (bio || '').length }}/500</p>
        </div>
      </div>

      <!-- ──────────── Open for Collaborations ──────────── -->
      <div class="flex items-start justify-between gap-3 p-3 rounded-lg border border-cyan-400/15 bg-cyan-400/[0.04]">
        <div class="flex-1 min-w-0">
          <label class="block text-xs font-semibold text-white">{{ $t('chat.settings.collab_label') }}</label>
          <p class="text-[11px] text-text-dim mt-1 leading-snug">{{ $t('chat.settings.collab_hint') }}</p>
        </div>
        <el-switch v-model="openForCollabs" size="default" active-color="#00f2ff" />
      </div>

      <!-- ──────────── Audio devices ──────────── -->
      <div class="pt-3 border-t border-white/5 space-y-4">
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-xs font-semibold text-text-muted">Audio devices</label>
            <button
              v-if="!devicePermissionGranted"
              type="button"
              class="text-[10px] uppercase tracking-widest font-semibold text-primary hover:text-white disabled:opacity-40"
              :disabled="permissionRequesting"
              @click="requestDevicePermission"
            >
              {{ permissionRequesting ? 'Requesting…' : 'Allow access' }}
            </button>
          </div>
          <p v-if="!devicesSupported" class="text-[11px] text-red-300/80">
            This browser does not expose audio devices. (Requires a secure context — HTTPS or localhost.)
          </p>
          <p v-else-if="!devicePermissionGranted" class="text-[11px] text-text-dim">
            Click "Allow access" so the browser reveals device names. Until you do, the dropdowns only show generic labels.
          </p>
          <p v-else-if="devicesError" class="text-[11px] text-red-300/80">{{ devicesError }}</p>
        </div>

        <!-- Microphone -->
        <div class="space-y-1.5">
          <div class="flex items-center justify-between">
            <label class="text-xs font-semibold text-text-muted">Microphone</label>
            <span v-if="micTesting" class="text-[10px] text-primary">Listening… {{ micLevel }}%</span>
          </div>
          <div class="flex items-center gap-2">
            <el-select
              v-model="audioInputDeviceId"
              class="flex-1"
              size="small"
              :disabled="!devicesSupported"
              :placeholder="devicePermissionGranted ? 'System default' : 'Allow access first'"
            >
              <el-option label="System default" :value="DEVICE_DEFAULT" />
              <el-option
                v-for="d in audioInputs"
                :key="d.deviceId"
                :label="d.label || ('Microphone ' + d.deviceId.slice(0, 6))"
                :value="d.deviceId"
              />
            </el-select>
            <button
              type="button"
              class="shrink-0 w-8 h-8 rounded-md bg-white/5 text-text-muted hover:text-white hover:bg-white/10 flex items-center justify-center disabled:opacity-40"
              :title="micTesting ? 'Stop test' : 'Test microphone'"
              :disabled="!devicePermissionGranted"
              @click="toggleMicTest"
            >
              <el-icon size="12"><Microphone /></el-icon>
            </button>
          </div>
          <div v-if="micTesting" class="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div class="h-full bg-primary transition-[width] duration-75" :style="{ width: micLevel + '%' }"></div>
          </div>
        </div>

        <!-- Speakers -->
        <div class="space-y-1.5">
          <div class="flex items-center justify-between">
            <label class="text-xs font-semibold text-text-muted">Speakers</label>
            <span v-if="!setSinkIdSupported" class="text-[10px] text-text-dim">(browser uses system default)</span>
          </div>
          <div class="flex items-center gap-2">
            <el-select
              v-model="audioOutputDeviceId"
              class="flex-1"
              size="small"
              :disabled="!setSinkIdSupported || !devicesSupported"
              :placeholder="setSinkIdSupported ? 'System default' : 'Not supported in this browser'"
            >
              <el-option label="System default" :value="DEVICE_DEFAULT" />
              <el-option
                v-for="d in audioOutputs"
                :key="d.deviceId"
                :label="d.label || ('Speakers ' + d.deviceId.slice(0, 6))"
                :value="d.deviceId"
              />
            </el-select>
            <button
              type="button"
              class="shrink-0 w-8 h-8 rounded-md bg-white/5 text-text-muted hover:text-white hover:bg-white/10 flex items-center justify-center"
              :title="setSinkIdSupported ? 'Test speaker' : 'Test speaker (system default)'"
              @click="testSpeaker"
            >
              <el-icon size="12"><VideoPlay /></el-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- ──────────── Sound settings ──────────── -->
      <div class="pt-3 border-t border-white/5 space-y-4">
        <div>
          <label class="block text-xs font-semibold text-text-muted mb-2">Sound volume</label>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-colors"
              :class="audio.volume === 0 ? 'bg-red-500/20 text-red-300' : 'bg-white/5 text-text-muted hover:text-white'"
              :title="audio.volume === 0 ? 'Unmute' : 'Mute'"
              @click="toggleMute"
            >
              <el-icon size="14"><Mute v-if="audio.volume === 0" /><Microphone v-else /></el-icon>
            </button>
            <el-slider
              v-model="audio.volume"
              :min="0"
              :max="100"
              :step="1"
              :show-tooltip="false"
              class="flex-1"
            />
            <span class="shrink-0 w-8 text-right text-[11px] text-text-muted tabular-nums">{{ audio.volume }}%</span>
          </div>
        </div>

        <!-- Message sound -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <label class="text-xs font-semibold text-text-muted">Message sound</label>
            <el-switch v-model="audio.messageEnabled" size="small" />
          </div>
          <div class="flex items-center gap-2">
            <el-select v-model="audio.messageSoundId" class="flex-1" :disabled="!audio.messageEnabled" size="small">
              <el-option-group label="Built-in">
                <el-option v-for="p in MESSAGE_SOUND_PRESETS" :key="p.id" :label="p.name" :value="p.id" />
              </el-option-group>
              <el-option-group v-if="customSounds.length" label="Custom uploads">
                <el-option
                  v-for="c in customSounds"
                  :key="c.id"
                  :label="c.name"
                  :value="'custom:' + c.id"
                />
              </el-option-group>
            </el-select>
            <button
              type="button"
              class="shrink-0 w-8 h-8 rounded-md bg-white/5 text-text-muted hover:text-white hover:bg-white/10 flex items-center justify-center"
              title="Test sound"
              :disabled="!audio.messageEnabled"
              @click="testSound(audio.messageSoundId, 'message')"
            >
              <el-icon size="12"><VideoPlay /></el-icon>
            </button>
          </div>
        </div>

        <!-- Call ringtone -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <label class="text-xs font-semibold text-text-muted">Call ringtone</label>
            <el-switch v-model="audio.callEnabled" size="small" />
          </div>
          <div class="flex items-center gap-2">
            <el-select v-model="audio.callSoundId" class="flex-1" :disabled="!audio.callEnabled" size="small">
              <el-option-group label="Built-in">
                <el-option v-for="p in CALL_SOUND_PRESETS" :key="p.id" :label="p.name" :value="p.id" />
              </el-option-group>
              <el-option-group v-if="customSounds.length" label="Custom uploads">
                <el-option
                  v-for="c in customSounds"
                  :key="c.id"
                  :label="c.name"
                  :value="'custom:' + c.id"
                />
              </el-option-group>
            </el-select>
            <button
              type="button"
              class="shrink-0 w-8 h-8 rounded-md bg-white/5 text-text-muted hover:text-white hover:bg-white/10 flex items-center justify-center"
              title="Test sound"
              :disabled="!audio.callEnabled"
              @click="testSound(audio.callSoundId, 'call')"
            >
              <el-icon size="12"><VideoPlay /></el-icon>
            </button>
          </div>
        </div>

        <!-- Custom uploads -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="text-xs font-semibold text-text-muted">Custom sounds ({{ customSounds.length }}/5)</label>
            <button
              type="button"
              class="text-[10px] uppercase tracking-widest font-semibold text-primary hover:text-white disabled:opacity-40"
              :disabled="uploading || customSounds.length >= 5"
              @click="soundFileEl?.click()"
            >
              {{ uploading ? 'Uploading…' : '+ Upload' }}
            </button>
            <input
              ref="soundFileEl"
              type="file"
              class="hidden"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,audio/mp4,audio/aac"
              @change="onSoundPicked"
            />
          </div>
          <p class="text-[10px] text-text-dim mb-1.5">MP3/WAV/OGG/M4A, max 500 KB.</p>
          <div v-if="customSounds.length" class="space-y-1">
            <div
              v-for="c in customSounds"
              :key="c.id"
              class="flex items-center gap-2 px-2 py-1.5 rounded-md bg-black/20 border border-white/5"
            >
              <el-icon size="11" class="text-text-dim"><Headset /></el-icon>
              <span class="flex-1 truncate text-[11px] text-white">{{ c.name }}</span>
              <span class="shrink-0 text-[10px] text-text-dim">{{ formatKb(c.sizeBytes) }}</span>
              <button
                type="button"
                class="shrink-0 w-6 h-6 rounded flex items-center justify-center text-text-dim hover:text-white hover:bg-white/10"
                title="Preview"
                @click="testSound('custom:' + c.id, 'message')"
              >
                <el-icon size="10"><VideoPlay /></el-icon>
              </button>
              <button
                type="button"
                class="shrink-0 w-6 h-6 rounded flex items-center justify-center text-text-dim hover:text-red-400 hover:bg-red-500/10"
                title="Remove"
                @click="removeCustomSound(c.id)"
              >
                <el-icon size="10"><Close /></el-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm text-text-muted hover:text-white bg-white/5"
          :disabled="busy"
          @click="visible = false"
        >
          {{ $t('chat.settings.cancel') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-md text-sm font-semibold bg-primary text-black hover:bg-primary/90 disabled:opacity-50"
          :disabled="busy"
          @click="save"
        >
          {{ busy ? '…' : $t('chat.settings.save') }}
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Mute, Microphone, VideoPlay, Headset, Close } from '@element-plus/icons-vue'
import { useChatTranslation } from '~/composables/chat/useChatTranslation'
import { useChatSocket } from '~/composables/chat/useChatSocket'
import { useChatAudio, MESSAGE_SOUND_PRESETS, CALL_SOUND_PRESETS } from '~/composables/chat/useChatAudio'

/**
 * Chat settings dialog. Currently only exposes the auto-translate target
 * language — future preferences (sound, notifications) should slot into
 * the same dialog behind horizontal tabs.
 *
 * On first open with no prior preference, we pre-select the best match for
 * the browser's `navigator.language` (e.g. `de-AT` → `de`) but don't save
 * until the user clicks Save. This avoids silently flipping translation on
 * for someone who just wanted to peek at the setting.
 */

const OFF_VALUE = '__off__'
// Sentinel for "use system default" in the device pickers. Mongoose stores
// this as `null` server-side; the UI uses an empty string so el-select can
// model it as a regular option (null collapses with v-model).
const DEVICE_DEFAULT = ''

const props = defineProps({
  modelValue: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue'])

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
})

const { user, fetch: refreshSession } = useUserSession()
const { $toast } = useNuxtApp()
const { t } = useI18n()
const { clearAndRehydrate } = useChatTranslation()

const selected = ref(OFF_VALUE)
const supportedLanguages = ref([])
const busy = ref(false)

/* ─────────── Sound settings (volume + toggles + presets + uploads) ─────────── */

const { test: testSoundFromComposable } = useChatAudio()
const soundFileEl = ref(null)
const uploading = ref(false)

// Local editable state — seeded from `user.chatProfile` on dialog open.
// Saved back on Save button.
const audio = reactive({
  volume: 70,
  messageEnabled: true,
  messageSoundId: 'default',
  callEnabled: true,
  callSoundId: 'default',
})

// "Open for collaborations" public flag. Seeded from the settings GET on
// dialog open; persisted via settings PUT alongside audio prefs.
const openForCollabs = ref(false)

// Public profile text — display name override, custom status, bio. Seeded from
// the settings GET (server-authoritative) on open; persisted via settings PUT.
const displayName = ref('')
const customStatus = ref('')
const bio = ref('')

/* ─────────── Audio device pickers ─────────── */
//
// Two challenges with browser device APIs:
//
// 1. `enumerateDevices()` returns `label: ''` until the page has been
//    granted mic permission at least once for this origin. So we expose
//    an explicit "Allow access" button that calls getUserMedia({audio})
//    once just to unlock the labels — we throw the stream away
//    immediately (no recording happens).
//
// 2. `setSinkId()` — the API for routing audio to a specific output
//    device — is implemented in Chromium-based browsers but not Firefox
//    (as of 2026). When unsupported we still let the user PICK a value
//    (so when they later switch to a supporting browser the preference
//    sticks) but show a hint, and the playback paths just skip the
//    setSinkId call.

const devicesSupported = ref(true)
const devicePermissionGranted = ref(false)
const permissionRequesting = ref(false)
const devicesError = ref('')
const audioInputs = ref([])
const audioOutputs = ref([])
const audioInputDeviceId = ref(DEVICE_DEFAULT)
const audioOutputDeviceId = ref(DEVICE_DEFAULT)
const setSinkIdSupported = ref(false)

let deviceChangeListener = null

async function enumerateAudioDevices() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    devicesSupported.value = false
    return
  }
  try {
    const all = await navigator.mediaDevices.enumerateDevices()
    audioInputs.value = all.filter(d => d.kind === 'audioinput')
    audioOutputs.value = all.filter(d => d.kind === 'audiooutput')
    // Labels are non-empty once permission has been granted at least once.
    // Firefox/Safari only reveal labels while an active capture is held —
    // so after requestDevicePermission stops the temp stream, labels can
    // disappear again. Treat this flag as sticky-on: requestDevicePermission
    // also flips it true explicitly so a successful prompt isn't unset by
    // a subsequent enumerate.
    const hasLabels = audioInputs.value.some(d => !!d.label)
    if (hasLabels) devicePermissionGranted.value = true
    devicesError.value = ''

    // Reconcile saved ids against what the browser actually exposes. Chrome
    // rotates deviceIds across sessions for privacy unless persistent
    // permission is granted, so a deviceId saved months ago may no longer
    // match anything in the list. Without this the el-select binds to a
    // dangling id and renders an empty/garbled dropdown. Only reconcile
    // once permission is granted — until then the list is intentionally
    // unlabelled and we shouldn't drop a still-valid saved preference.
    if (devicePermissionGranted.value) {
      if (audioInputDeviceId.value
          && audioInputDeviceId.value !== DEVICE_DEFAULT
          && !audioInputs.value.some(d => d.deviceId === audioInputDeviceId.value)) {
        audioInputDeviceId.value = DEVICE_DEFAULT
      }
      if (audioOutputDeviceId.value
          && audioOutputDeviceId.value !== DEVICE_DEFAULT
          && !audioOutputs.value.some(d => d.deviceId === audioOutputDeviceId.value)) {
        audioOutputDeviceId.value = DEVICE_DEFAULT
      }
    }
  } catch (err) {
    // Permission was revoked or the API broke — clear stale state so the
    // UI doesn't keep advertising labelled devices the user no longer has
    // access to.
    audioInputs.value = []
    audioOutputs.value = []
    devicePermissionGranted.value = false
    devicesError.value = err?.message || 'Could not list devices'
  }
}

async function requestDevicePermission() {
  if (permissionRequesting.value) return
  if (!navigator.mediaDevices?.getUserMedia) {
    devicesError.value = 'getUserMedia is not available in this context (HTTPS required).'
    return
  }
  permissionRequesting.value = true
  try {
    // We only need permission — release the stream immediately. Some
    // browsers require this trick to unlock labels in enumerateDevices().
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    // Flip the granted flag synchronously while we still have the active
    // capture; on Firefox/Safari the subsequent `enumerateDevices()` may
    // see labels go empty again once the temp stream is stopped, and the
    // label-based heuristic alone would leave the button stuck on
    // "Allow access".
    devicePermissionGranted.value = true
    stream.getTracks().forEach(t => t.stop())
    await enumerateAudioDevices()
  } catch (err) {
    devicesError.value = err?.name === 'NotAllowedError'
      ? 'Permission denied — enable microphone for this site in your browser.'
      : (err?.message || 'Permission request failed')
  } finally {
    permissionRequesting.value = false
  }
}

/* ─────────── Microphone level meter ─────────── */
//
// Live RMS-based level meter that runs only while the user is testing.
// Cleaned up via tearMicTest() on dialog close, mic-disable, or device
// change so we never leak a hot mic stream.

const micTesting = ref(false)
const micLevel = ref(0)
let micTestStream = null
let micTestCtx = null
let micTestRaf = 0
// Monotonic seq token: incremented by `tearMicTest` AND by each fresh
// `startMicTest` so an in-flight `getUserMedia` resolved after a teardown
// or device-switch can detect it lost the race and dispose the orphaned
// stream instead of overwriting `micTestStream` (hot-mic leak).
let micTestSeq = 0

async function startMicTest() {
  // Guard against re-entrance from a direct caller — the seq-token logic
  // handles concurrent in-flight starts, but if a test is already RUNNING
  // (gUM resolved, stream live) a second call would still overwrite
  // `micTestStream` and leak the previous stream's tracks.
  if (micTesting.value) return
  if (!navigator.mediaDevices?.getUserMedia) {
    devicesError.value = 'getUserMedia not available'
    return
  }
  const seq = ++micTestSeq
  let stream
  try {
    const constraints = {
      audio: audioInputDeviceId.value
        ? { deviceId: { exact: audioInputDeviceId.value } }
        : true
    }
    stream = await navigator.mediaDevices.getUserMedia(constraints)
    // Lost the race to a newer start or a teardown — dispose and bail.
    if (seq !== micTestSeq) {
      stream.getTracks().forEach(t => { try { t.stop() } catch { /* noop */ } })
      return
    }
    micTestStream = stream
    micTestCtx = new (window.AudioContext || window.webkitAudioContext)()
    const src = micTestCtx.createMediaStreamSource(micTestStream)
    const analyser = micTestCtx.createAnalyser()
    analyser.fftSize = 1024
    src.connect(analyser)
    const buf = new Uint8Array(analyser.fftSize)
    micTesting.value = true
    const loop = () => {
      if (!micTesting.value || seq !== micTestSeq) return
      analyser.getByteTimeDomainData(buf)
      // RMS deviation from the silent midpoint (128).
      let sum = 0
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128
        sum += v * v
      }
      const rms = Math.sqrt(sum / buf.length)
      // 0.0–~0.5 maps to 0–100% with a small floor lift so the bar
      // visibly responds to quiet speech.
      micLevel.value = Math.min(100, Math.round(rms * 200))
      micTestRaf = requestAnimationFrame(loop)
    }
    loop()
  } catch (err) {
    // Only the active attempt should clobber UI state / tear down.
    if (seq === micTestSeq) {
      devicesError.value = err?.message || 'Mic test failed'
      tearMicTest()
    } else if (stream) {
      stream.getTracks().forEach(t => { try { t.stop() } catch { /* noop */ } })
    }
  }
}

function tearMicTest() {
  // Bump seq so any in-flight startMicTest() loses the race and disposes
  // its stream instead of resurrecting state we're tearing down here.
  micTestSeq++
  micTesting.value = false
  micLevel.value = 0
  if (micTestRaf) {
    cancelAnimationFrame(micTestRaf)
    micTestRaf = 0
  }
  if (micTestStream) {
    micTestStream.getTracks().forEach(t => t.stop())
    micTestStream = null
  }
  if (micTestCtx) {
    try { micTestCtx.close() } catch { /* noop */ }
    micTestCtx = null
  }
}

function toggleMicTest() {
  if (micTesting.value) tearMicTest()
  else startMicTest()
}

/* ─────────── Speaker test ─────────── */

async function testSpeaker() {
  // Use the message-default ding for the test — it's short and unmistakable.
  const url = MESSAGE_SOUND_PRESETS[0]?.url
  if (!url) return
  try {
    const a = new Audio(url)
    a.volume = audio.volume / 100
    if (audioOutputDeviceId.value && typeof a.setSinkId === 'function') {
      try {
        await a.setSinkId(audioOutputDeviceId.value)
      } catch (err) {
        // setSinkId can reject for unauthorised device IDs (rare). Fall
        // through and let the audio play on the default sink — better
        // than silent failure during a test action.
        console.warn('[chat:settings] setSinkId failed for test', err)
      }
    }
    await a.play()
  } catch (err) {
    console.warn('[chat:settings] speaker test failed', err)
  }
}

// Pre-mute snapshot — if the user hits the mute button with volume>0, we
// remember the previous value so an unmute restores it instead of forcing 70%.
let preMuteVolume = 70

const customSounds = computed(() => (user.value?.chatProfile?.customSounds || []))

function toggleMute() {
  if (audio.volume > 0) {
    preMuteVolume = audio.volume
    audio.volume = 0
  } else {
    audio.volume = preMuteVolume || 70
  }
}

function testSound(soundId, kind) {
  // Use the user's current picked volume (not the saved one) for live preview
  const override = audio.volume / 100
  const preset = kind === 'message' ? MESSAGE_SOUND_PRESETS : CALL_SOUND_PRESETS
  const hit = preset.find(p => p.id === soundId)
  if (hit) {
    const a = new Audio(hit.url)
    a.volume = override
    a.play().catch(() => {})
    return
  }
  // Fall through for custom sounds via the composable (it resolves the URL)
  testSoundFromComposable(soundId, kind)
}

function formatKb(bytes) {
  if (!bytes) return ''
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

async function onSoundPicked(e) {
  const file = e.target.files?.[0]
  e.target.value = ''
  if (!file) return
  if (file.size > 512 * 1024) {
    $toast?.error('Too large — max 500 KB')
    return
  }
  uploading.value = true
  try {
    const presign = await $fetch('/api/chat/sounds/presign', {
      method: 'POST',
      body: { fileName: file.name, contentType: file.type, bytes: file.size }
    })
    await $fetch(presign.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type }
    })
    await $fetch('/api/chat/sounds/confirm', {
      method: 'POST',
      body: { key: presign.key, name: file.name.replace(/\.[^.]+$/, '').slice(0, 40), sizeBytes: file.size }
    })
    await refreshSession()
    $toast?.success('Sound uploaded')
  } catch (err) {
    console.error('[chat:sounds] upload failed', err)
    $toast?.error(err?.data?.statusMessage || 'Upload failed')
  } finally {
    uploading.value = false
  }
}

async function removeCustomSound(id) {
  try {
    await $fetch(`/api/chat/sounds/${id}`, { method: 'DELETE' })
    await refreshSession()
    // Reset any selection that pointed at the deleted sound
    const ref = `custom:${id}`
    if (audio.messageSoundId === ref) audio.messageSoundId = 'default'
    if (audio.callSoundId === ref)    audio.callSoundId    = 'default'
  } catch (err) {
    console.error('[chat:sounds] delete failed', err)
    $toast?.error(err?.data?.statusMessage || 'Delete failed')
  }
}

/* ─────────── Chat ID ─────────── */
//
// Two sources of truth:
//  1. `GET /api/chat/settings` when the dialog opens (covers the case where
//     the user already connected to chat in a previous session)
//  2. The WS `identity` frame (emitted once per connect; covers the first-
//     connect case where the id was just minted)
// We prefer whichever arrives first and keep both listeners registered so
// the dialog can stay open during a reconnect.
const chatIdRef = ref(null)
const copied = ref(false)
const { on: onWsFrame } = useChatSocket()
let offIdentity = null
onMounted(() => {
  offIdentity = onWsFrame('identity', (frame) => {
    if (typeof frame?.chatId === 'string') chatIdRef.value = frame.chatId
  })
})
onBeforeUnmount(() => {
  if (offIdentity) offIdentity()
  // Hard safety net — `destroy-on-close` will fire onBeforeUnmount even
  // when the watcher above already cleaned up. Guarded teardown is cheap.
  tearMicTest()
  if (deviceChangeListener && typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
    navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener)
    deviceChangeListener = null
  }
})

const displayedChatId = computed(() => chatIdRef.value || null)
const displayedChatIdFormatted = computed(() => {
  const id = displayedChatId.value
  if (!id || id.length !== 8) return id || ''
  return `${id.slice(0, 4)}-${id.slice(4)}`
})

async function copyChatId() {
  const id = displayedChatIdFormatted.value
  if (!id) return
  // HTTP dev servers don't expose `navigator.clipboard` — fall back to the
  // legacy `execCommand` path via a hidden textarea so Copy works for
  // everyone. Same helper is duplicated in ChatPanel.vue header.
  const ok = await writeClipboard(id)
  if (ok) {
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } else {
    $toast?.error(t('chat.settings.chat_id_copy_failed') || 'Could not copy')
  }
}

async function writeClipboard(text) {
  try {
    if (typeof window !== 'undefined' && window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

function pickBrowserDefault(langs) {
  try {
    const browser = (navigator?.language || '').toLowerCase().split(/[-_]/)[0]
    if (!browser) return null
    return langs.find(l => l.code === browser)?.code || null
  } catch {
    return null
  }
}

async function onOpen() {
  busy.value = true
  try {
    const res = await $fetch('/api/chat/settings')
    supportedLanguages.value = Array.isArray(res?.supportedLanguages) ? res.supportedLanguages : []
    if (typeof res?.chatId === 'string') chatIdRef.value = res.chatId
    const existing = res?.preferredLanguage
    if (existing) {
      selected.value = existing
    } else {
      // No saved preference yet — suggest the browser language if supported
      const suggestion = pickBrowserDefault(supportedLanguages.value)
      selected.value = suggestion || OFF_VALUE
    }
    // Seed local audio state from the current chatProfile
    const prof = user.value?.chatProfile || {}
    audio.volume = typeof prof.soundVolume === 'number' ? prof.soundVolume : 70
    audio.messageEnabled = prof.messageSoundEnabled !== false
    audio.messageSoundId = prof.messageSoundId || 'default'
    audio.callEnabled    = prof.callSoundEnabled !== false
    audio.callSoundId    = prof.callSoundId || 'default'
    preMuteVolume = audio.volume > 0 ? audio.volume : 70
    // Server-authoritative for openForCollabs (the GET pulls the saved
    // value from Mongo; we never trust only the session cache here
    // because the cache might be stale after another tab toggled it).
    openForCollabs.value = !!res?.openForCollabs
    displayName.value = res?.displayName || ''
    customStatus.value = res?.customStatus || ''
    bio.value = res?.bio || ''

    // Audio devices — server returns the saved ids; null means default.
    audioInputDeviceId.value  = res?.audioInputDeviceId  || DEVICE_DEFAULT
    audioOutputDeviceId.value = res?.audioOutputDeviceId || DEVICE_DEFAULT
    // Detect setSinkId support once. The output picker is still shown when
    // unsupported so the preference persists across browser changes, but
    // we annotate it visibly.
    setSinkIdSupported.value = typeof window !== 'undefined'
      && typeof HTMLAudioElement !== 'undefined'
      && typeof HTMLAudioElement.prototype.setSinkId === 'function'
    await enumerateAudioDevices()
    // Keep the picker live across hardware changes (USB headset hot-plug).
    // Idempotent: if onOpen ever fires twice on the same instance (e.g. a
    // future caller re-emits the event), the previous registration would
    // otherwise leak and fire forever against a torn-down enumerate fn.
    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      if (deviceChangeListener && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener)
      }
      deviceChangeListener = () => { enumerateAudioDevices() }
      navigator.mediaDevices.addEventListener('devicechange', deviceChangeListener)
    }
  } catch (err) {
    console.error('[chat:settings] load failed', err)
    $toast?.error(t('chat.settings.load_failed') || 'Could not load settings')
  } finally {
    busy.value = false
  }
}

// Stop the mic-test stream + drop the devicechange listener whenever the
// dialog closes. `destroy-on-close` on el-dialog re-mounts the component
// next open, but the AudioContext + getUserMedia stream are owned at the
// composable level — they outlive a v-if flip if not torn down explicitly.
watch(visible, (v) => {
  if (!v) {
    tearMicTest()
    if (deviceChangeListener && navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', deviceChangeListener)
      deviceChangeListener = null
    }
  }
})

// Restart the mic-test against the new device when the user changes the
// dropdown mid-test. Without this the bar would keep reading from the
// previous mic until the user toggled the test off and on.
watch(audioInputDeviceId, () => {
  if (micTesting.value) {
    tearMicTest()
    startMicTest()
  }
})

async function save() {
  busy.value = true
  try {
    const value = selected.value === OFF_VALUE ? null : selected.value
    await $fetch('/api/chat/settings', {
      method: 'PUT',
      body: {
        preferredLanguage: value,
        soundVolume: audio.volume,
        messageSoundEnabled: audio.messageEnabled,
        messageSoundId: audio.messageSoundId,
        callSoundEnabled: audio.callEnabled,
        callSoundId: audio.callSoundId,
        openForCollabs: openForCollabs.value,
        // Public profile text — server sanitizes + caps + clears on empty.
        displayName: displayName.value,
        customStatus: customStatus.value,
        bio: bio.value,
        // Empty-string sentinel collapses to null on the server (which is
        // how "system default" is stored in the user doc).
        audioInputDeviceId: audioInputDeviceId.value || null,
        audioOutputDeviceId: audioOutputDeviceId.value || null,
      }
    })
    // Refresh session so `user.chatProfile.*` is up to date everywhere
    await refreshSession()
    // Wipe cached translations so visible messages re-hydrate against the
    // new target language
    clearAndRehydrate()
    $toast?.success(t('chat.settings.saved'))
    visible.value = false
  } catch (err) {
    console.error('[chat:settings] save failed', err)
    $toast?.error(err?.data?.statusMessage || t('chat.settings.save_failed') || 'Save failed')
  } finally {
    busy.value = false
  }
}
</script>
